import { FieldViolationsView } from "@/components/views/FieldViolationsView";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { useScrollToTopOnLoad } from "@/utils/hooksUtils";
import { App as AntApp, Card, Form } from "antd";
import merge from "lodash/merge";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRealtyGenerate, useValidateClient, useValidateClients } from "../../../../../../common/api/queries";
import { Permission } from "../../../../../../common/security/enums";
import { FieldConstraintViolation } from "../../../../../../common/types";
import type { UUID } from "../../../../../../typings/global";
import { useCurrentAgentAccount } from "../../../../../auth/hooks";
import { CalcValidationGroup, ClientFormType, ClientType } from "../../../../../client/enums";
import {
  CreateUpdateContractClient,
  CreateUpdateNaturalClient,
  LegalClient,
  NaturalClient,
  SelfEmployedClient,
} from "../../../../../client/types";
import { clientToCreateUpdateContractClient, processClientsDataViolations } from "../../../../../client/utils";
import { InstitutionEnum } from "../../../../../institution/enums";
import { CalcType, ClientExperience } from "../../../../enums";
import { CalcGenResultModal } from "../../../components/CalcGenResultModal";
import { CalcNavigation } from "../../../components/CalcNavigation";
import { CalcSummaryModal } from "../../../components/summary/CalcSummaryModal";
import { CalcResult, GenGainersData, GenResponse } from "../../../types";
import { processGenResultError } from "../../../utils";
import { useRealtyCalcResults } from "../../contexts";
import {
  RealtyCalc,
  RealtyCalcEquipmentReinsurance,
  RealtyCalcResultData,
  RealtyFormClients,
  RealtyGen,
  RealtyGenForm,
} from "../../types";
import { RealtyGenClientsDataSection } from "./sections/RealtyGenClientsDataSection";
import { RealtyGenOtherDataSection } from "./sections/RealtyGenOtherDataSection";

interface Props {
  initialData?: RealtyGenForm;
  calcData: RealtyCalc;
  clients: RealtyFormClients;
  selectedResult: CalcResult<RealtyCalcResultData>;
  draftId?: UUID;
  onClientChange: (
    type: ClientFormType,
    client?: Partial<NaturalClient> | Partial<SelfEmployedClient> | Partial<LegalClient>,
  ) => RealtyFormClients;
  handleGoToPreviousStep: () => void;
}

export const RealtyGenWrapper = (props: Props) => {
  useScrollToTopOnLoad();

  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const { calcResults, calcResultsGroupedByInstitution } = useRealtyCalcResults();

  const [generateResponse, setGenerateResponse] = useState<GenResponse>();
  const [summaryOpen, setSummaryOpen] = useState<boolean>(false);
  const [realtyGenFormData, setRealtyGenFormData] = useState<RealtyGenForm>();
  const [clientsViolationErrors, setClientsViolationErrors] = useState<Map<ClientFormType, FieldConstraintViolation[]>>(
    () => new Map(),
  );

  const userAccount = useCurrentAgentAccount();

  const [form] = Form.useForm<RealtyGenForm>();
  const clientValidation = useValidateClient();
  const clientsValidation = useValidateClients();
  const realtyGenerate = useRealtyGenerate();

  const createClientsIndicesMap = (clients: RealtyFormClients): Map<ClientFormType, number> => {
    const { policyHolder, insured, representative, vinculation } = clients;
    const policyHolderIsAlsoInsured = form.getFieldValue(["clientsData", "policyHolderIsAlsoInsured"]);
    const indices = new Map<ClientFormType, number>();
    let index = 0;

    indices.set(ClientFormType.POLICY_HOLDER, index++);

    if (policyHolderIsAlsoInsured) {
      indices.set(ClientFormType.INSURED, index - 1);
    } else if (policyHolder?.type === ClientType.NATURAL && insured) {
      indices.set(ClientFormType.INSURED, index++);
    }

    if (policyHolder?.type === ClientType.LEGAL && representative) {
      indices.set(ClientFormType.REPRESENTATIVE, index++);
    }

    if (props.calcData.generalInsuranceData.vinculation && vinculation) {
      indices.set(ClientFormType.VINCULATION, index);
    }

    return indices;
  };

  const createContractClientsArray = (clients: RealtyFormClients): CreateUpdateContractClient[] => {
    const { policyHolder, insured, representative, vinculation } = clients;
    const policyHolderIsAlsoInsured = form.getFieldValue(["clientsData", "policyHolderIsAlsoInsured"]);
    const createUpdateClients: CreateUpdateContractClient[] = [];

    createUpdateClients.push(
      clientToCreateUpdateContractClient({
        ...policyHolder,
        identityCardNumber: form.getFieldValue(["clientsData", "policyHolderIdentityCardNumber"]),
      } as NaturalClient),
    );

    if (!policyHolderIsAlsoInsured && policyHolder?.type === ClientType.NATURAL && insured) {
      createUpdateClients.push(clientToCreateUpdateContractClient(insured));
    }

    if (policyHolder?.type === ClientType.LEGAL && representative) {
      createUpdateClients.push(clientToCreateUpdateContractClient(representative));
    }

    if (props.calcData.generalInsuranceData.vinculation && vinculation) {
      createUpdateClients.push(clientToCreateUpdateContractClient(vinculation));
    }

    return createUpdateClients;
  };

  useEffect(() => {
    if (props.initialData) {
      const formData = structuredClone(props.initialData);

      if (formData.realtyInsuranceData) {
        formData.realtyInsuranceData = {
          ...formData.realtyInsuranceData,
          garageReinsurance: formData.realtyInsuranceData.garageReinsurance
            ? {
                ...formData.realtyInsuranceData.garageReinsurance,
                underConstruction: !!formData.realtyInsuranceData.garageReinsurance.underConstruction,
              }
            : undefined,
        };
      }

      if (props.calcData.householdInsuranceData) {
        formData.householdInsuranceData = {
          ...formData.householdInsuranceData,
          equipmentReinsurances: (
            props.calcData.householdInsuranceData.equipmentReinsurances || []
          ).map<RealtyCalcEquipmentReinsurance>((e, i) => ({
            type: e.type,
            insuranceAmount: e.insuranceAmount,
            manufacturer: formData.householdInsuranceData?.equipmentReinsurances?.[i]?.manufacturer,
            model: formData.householdInsuranceData?.equipmentReinsurances?.[i]?.model,
            serialNumber: formData.householdInsuranceData?.equipmentReinsurances?.[i]?.serialNumber,
            otherDesc: formData.householdInsuranceData?.equipmentReinsurances?.[i]?.otherDesc,
          })),
        };
      }

      const policyHolder = props.clients.policyHolder;

      if (
        policyHolder?.type !== ClientType.NATURAL ||
        props.selectedResult.insuranceInstitution.institutionEnum === InstitutionEnum.GENERALI
      ) {
        formData.clientsData.policyHolderIsAlsoInsured = true;
      }

      if (policyHolder?.type !== ClientType.LEGAL) {
        delete formData.clientsData.representativeIdentifier;
        delete formData.clientsData.representativeFunction;
        if (props.clients.representative) {
          props.onClientChange(ClientFormType.REPRESENTATIVE);
        }
      }

      formData.financialMediationData = {
        ...formData.financialMediationData,
        clientExperience: (policyHolder?.type === ClientType.SELF_EMPLOYED || policyHolder?.type === ClientType.LEGAL
          ? ClientExperience.SUFFICIENT
          : formData.financialMediationData.clientExperience === ClientExperience.SUFFICIENT
            ? undefined
            : formData.financialMediationData.clientExperience) as ClientExperience,
        recommendedResult: undefined,
      };

      form.setFieldsValue(formData as RealtyGenForm);

      const clients = createContractClientsArray(props.clients);
      if (clients.length > 1) {
        clientsValidation.mutate(
          {
            prefix: "clientsData.clients",
            clients,
            validationGroup: CalcValidationGroup.GENERATE,
          },
          {
            onError: (error) => {
              if (error.violations) {
                const clientsIndicesMap = createClientsIndicesMap(props.clients);
                setClientsViolationErrors(
                  new Map([
                    ...clientsViolationErrors,
                    ...processClientsDataViolations(clientsIndicesMap, "clientsData.clients", error.violations),
                  ]),
                );

                const policyHolderIdentityCardNumberViolations = error.violations
                  .filter(
                    (violation) =>
                      violation.fieldPath ===
                      `clientsData.clients[${clientsIndicesMap.get(ClientFormType.POLICY_HOLDER)}].client.identityCardNumber`,
                  )
                  .map((violation) => ({ ...violation, fieldPath: "clientsData.policyHolderIdentityCardNumber" }));

                if (policyHolderIdentityCardNumberViolations?.length) {
                  setErrorsToForm(form, "calc.realty.attrs", policyHolderIdentityCardNumberViolations);
                }
              }
            },
          },
        );
      }
    } else {
      const policyHolder = props.calcData.clientsData.clients?.[props.calcData.clientsData.policyHolderIndex as number]
        ?.client as CreateUpdateNaturalClient;

      form.setFieldsValue({
        clientsData: {
          policyHolderIdentityCardNumber:
            policyHolder.identityCardNumber !== policyHolder.previousIdentityCardNumber
              ? policyHolder.identityCardNumber
              : undefined,
        },
        householdInsuranceData: props.calcData.householdInsuranceData
          ? { equipmentReinsurances: props.calcData.householdInsuranceData.equipmentReinsurances || [] }
          : undefined,
      });
    }
  }, []);

  const handleClientChange = (
    type: ClientFormType,
    client?: Partial<NaturalClient> | Partial<SelfEmployedClient> | Partial<LegalClient>,
  ) => {
    const clients = props.onClientChange(type, client);
    if (client) {
      clientValidation.mutate(
        {
          prefix: `clientsData.clients[${createClientsIndicesMap(clients).get(type)}]`,
          client: clientToCreateUpdateContractClient(client),
          validationGroup: CalcValidationGroup.GENERATE,
        },
        {
          onError: (error) => {
            if (error.violations) {
              const clientsIndicesMap = createClientsIndicesMap(props.clients);
              setClientsViolationErrors(
                new Map([
                  ...clientsViolationErrors,
                  ...processClientsDataViolations(clientsIndicesMap, "clientsData.clients", error.violations),
                ]),
              );

              const policyHolderIdentityCardNumberViolations = error.violations
                ?.filter(
                  (violation) =>
                    violation.fieldPath ===
                    `clientsData.clients[${clientsIndicesMap.get(ClientFormType.POLICY_HOLDER)}].client.identityCardNumber`,
                )
                .map((violation) => ({ ...violation, fieldPath: "clientsData.policyHolderIdentityCardNumber" }));

              if (policyHolderIdentityCardNumberViolations?.length) {
                setErrorsToForm(form, "calc.realty.attrs", policyHolderIdentityCardNumberViolations);
              }
            }
          },
        },
      );
    }
  };

  const handleClientViolationErrorsChange = (type: ClientFormType, violations?: FieldConstraintViolation[]) => {
    const updatedViolationsErrors = new Map<ClientFormType, FieldConstraintViolation[]>([...clientsViolationErrors]);
    if (violations && violations?.length > 0) {
      updatedViolationsErrors.set(type, violations);
    } else {
      updatedViolationsErrors.delete(type);
    }
    setClientsViolationErrors(updatedViolationsErrors);
  };

  const handleGenerateFormSubmit = () => {
    form
      .validateFields()
      .then(() => {
        if (checkHasClientErrors()) {
          notification.error({
            title: t("common.error"),
            description: t("common.validations.formError"),
          });
        } else {
          setSummaryOpen(true);
          setRealtyGenFormData(form.getFieldsValue());
        }
      })
      .catch((errorInfo) => {
        notification.error({ title: t("common.error"), description: t("common.validations.formError") });
        resolveFormValidationError(errorInfo);
      });
  };

  const handleGenerateFormApprovalsSubmit = (gainersData?: GenGainersData) => {
    setSummaryOpen(false);
    form
      .validateFields()
      .then(() => {
        if (checkHasClientErrors()) {
          notification.error({
            title: t("common.error"),
            description: t("common.validations.formError"),
          });
        } else {
          realtyGenerate.mutate(processAndGetGenFormData(gainersData), {
            onSuccess: (response) => {
              setGenerateResponse(response);

              if (response?.error) {
                const policyHolderIndex = createClientsIndicesMap(props.clients).get(ClientFormType.POLICY_HOLDER);

                const violations = processGenResultError(
                  {
                    ...response.error,
                    violations: response.error.violations?.map((violation) =>
                      violation.fieldPath === `clientsData.clients[${policyHolderIndex}].client.identityCardNumber`
                        ? { ...violation, fieldPath: "clientsData.policyHolderIdentityCardNumber" }
                        : violation,
                    ),
                  },
                  createClientsIndicesMap(props.clients),
                  form,
                  "calc.realty.attrs",
                );

                if (violations) {
                  notification.error({
                    title: response.error.message,
                    description: (
                      <FieldViolationsView
                        violations={violations.notificationViolations}
                        rootPath="calc.realty.attrs"
                      />
                    ),
                    duration: 10,
                  });
                  setClientsViolationErrors(violations.clientsViolations);
                }
              }
            },
          });
        }
      })
      .catch((errorInfo) => {
        notification.error({ title: t("common.error"), description: t("common.validations.formError") });
        resolveFormValidationError(errorInfo);
      });
  };

  const checkHasClientErrors = () => {
    const { policyHolder, insured, representative, vinculation } = props.clients;
    const policyHolderIsAlsoInsured = form.getFieldValue(["clientsData", "policyHolderIsAlsoInsured"]);

    if (!policyHolder || clientsViolationErrors.has(ClientFormType.POLICY_HOLDER)) {
      return true;
    }

    if (!policyHolderIsAlsoInsured && (!insured || clientsViolationErrors.has(ClientFormType.INSURED))) {
      return true;
    }

    if (
      policyHolder.type === ClientType.LEGAL &&
      (!representative || clientsViolationErrors.has(ClientFormType.REPRESENTATIVE))
    ) {
      return true;
    }

    // noinspection RedundantIfStatementJS
    if (
      props.calcData.generalInsuranceData.vinculation &&
      (!vinculation || clientsViolationErrors.has(ClientFormType.VINCULATION)) &&
      props.selectedResult.insuranceInstitution.institutionEnum !== InstitutionEnum.ALLIANZ
    ) {
      return true;
    }

    return false;
  };

  const processAndGetGenFormData = (gainersData?: GenGainersData): RealtyGen => {
    const clientsIndices = createClientsIndicesMap(props.clients);
    const values = form.getFieldsValue();

    const recommendedResultData = (values.financialMediationData.recommendedResult || []) as string[];
    const institutionResults = calcResultsGroupedByInstitution.find(
      (resultsRow) =>
        resultsRow.length > 0 &&
        resultsRow[0]?.insuranceInstitution.institutionEnum ===
          InstitutionEnum[recommendedResultData[0] as keyof typeof InstitutionEnum],
    );
    const recommendedResult =
      recommendedResultData.length > 1
        ? institutionResults?.find((result) => result.coverage === recommendedResultData[1])
        : institutionResults?.[0];

    return {
      calcResult: props.selectedResult.data as RealtyCalcResultData,
      calcResponse: { results: calcResults },
      coverage: props.selectedResult.coverage,
      insuranceInstitutionId: props.selectedResult.insuranceInstitution.id,
      draftId: props.draftId,
      ...merge(
        {},
        {
          ...props.calcData,
          householdInsuranceData: props.calcData.householdInsuranceData
            ? { ...props.calcData.householdInsuranceData, equipmentReinsurances: [] }
            : undefined,
        },
        {
          ...values,
          clientsData: {
            ...props.calcData.clientsData,
            clients: createContractClientsArray(props.clients),
            policyHolderIndex: clientsIndices.get(ClientFormType.POLICY_HOLDER),
            insuredClientIndex: clientsIndices.get(ClientFormType.INSURED) as number,
            representativeIndex: clientsIndices.get(ClientFormType.REPRESENTATIVE),
            representativeFunction: values.clientsData.representativeFunction,
            vinculationClientIndex: clientsIndices.get(ClientFormType.VINCULATION),
            policyHolderEmail: values.clientsData.policyHolderEmail,
            policyHolderPhone: values.clientsData.policyHolderPhone,
            policyHolderMarketingConsent: values.clientsData.policyHolderMarketingConsent,
          },
          financialMediationData: { ...values.financialMediationData, recommendedResult },
        },
      ),
      type: CalcType.REALTY,
      gainersData: gainersData ?? { gainer1Id: userAccount?.agent?.canBeGainer ? userAccount.agent.id : "" },
    };
  };

  const resolvePageTitleFirstLine = () => {
    const { selectedResult } = props;
    return `${t("calc.realty.titles.contractGeneration")} ${selectedResult.insuranceInstitution.name}${
      selectedResult.coverage ? `, ${t("calc.realty.sections.coverage")} ${selectedResult.coverage}` : ""
    }`;
  };

  const resolvePageTitleSecondLine = () => {
    const { calcData } = props;

    const insurancesTranslations = [];
    if (calcData.realtyInsuranceData) {
      insurancesTranslations.push(t("calc.realty.sections.realtyInsurance"));
    }
    if (calcData.householdInsuranceData) {
      insurancesTranslations.push(t("calc.realty.sections.householdInsurance"));
    }

    return `${t("calc.realty.enums.buildingType." + calcData.generalBuildingData.type)} - ${insurancesTranslations.join(
      ", ",
    )}`;
  };

  return (
    <>
      <Card
        className="card-box card-box--no-body"
        title={
          <>
            <h2>{resolvePageTitleFirstLine()}</h2>
            <h4 style={{ marginBottom: 0 }}>{resolvePageTitleSecondLine()}</h4>
          </>
        }
      />

      <Form form={form} layout="vertical" name="realtyGenForm">
        <RealtyGenClientsDataSection
          form={form}
          vinculation={props.calcData.generalInsuranceData.vinculation}
          selectedInstitutionEnum={props.selectedResult.insuranceInstitution.institutionEnum}
          clients={props.clients}
          clientsViolationErrors={clientsViolationErrors}
          onClientChange={handleClientChange}
          onClientViolationErrorsChange={handleClientViolationErrorsChange}
        />

        <RealtyGenOtherDataSection
          form={form}
          calcData={props.calcData}
          policyHolder={props.clients.policyHolder}
          selectedResult={props.selectedResult}
        />
      </Form>

      <CalcNavigation
        requiredPermission={Permission.REALTY_GENERATE_CONTRACT}
        onGenerateContract={handleGenerateFormSubmit}
        onGoToPrevious={props.handleGoToPreviousStep}
      />

      {realtyGenFormData ? (
        <CalcSummaryModal
          open={summaryOpen}
          data={{
            calcData: props.calcData,
            selectedCalcResult: props.selectedResult,
            clients: props.clients,
            formData: realtyGenFormData,
          }}
          onOkClick={(gainersData) => handleGenerateFormApprovalsSubmit(gainersData)}
          onCancelClick={() => setSummaryOpen(false)}
        />
      ) : undefined}

      <CalcGenResultModal result={generateResponse} onCancel={() => setGenerateResponse(undefined)} />
    </>
  );
};
